import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageHeader,
  StatCard,
  Card,
  Table,
  Button,
  Badge,
  Spinner,
  QueryError,
} from "../components";
import { usePolling } from "../hooks/usePolling";
import { apiGet, apiPost, apiDelete } from "../lib/api";
import { useToast } from "../contexts/ToastContext";
import { formatDateOnly, formatDateTime } from "../lib/display";

interface WaitlistEntry {
  id: string;
  email: string;
  source: string;
  status: "pending" | "approved";
  approved_at?: number | string | null;
  approved_by?: string | null;
  welcome_sent_at?: number | string | null;
  welcome_status?: "sent" | "failed" | "skipped" | null;
  created_at: number | string | null;
}

interface WaitlistResponse {
  entries: WaitlistEntry[];
  total: number;
  pending?: number;
  approved?: number;
}

export default function Waitlist() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<WaitlistResponse>({
    queryKey: ["waitlist"],
    queryFn: () => apiGet<WaitlistResponse>("/api/waitlist"),
  });

  const sendWelcome = useMutation({
    mutationFn: ({ id, email, resend }: { id: string; email: string; resend: boolean }) =>
      apiPost(`/api/waitlist/${id}/send-welcome`, { email, resend }),
    onSuccess: (result) => {
      const emailStatus = String((result as { emailStatus?: string })?.emailStatus ?? "sent");
      if (emailStatus === "sent") {
        toast.success("Waitlist approved and login email sent");
      } else if (emailStatus === "skipped") {
        toast.success("Waitlist approved (email provider not configured)");
      } else {
        toast.info("Waitlist approved, but email delivery failed");
      }
      queryClient.invalidateQueries({ queryKey: ["waitlist"] });
    },
    onError: (err: Error) => toast.error("Failed to approve waitlist entry", err.message),
  });

  const removeEntry = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/waitlist/${id}`),
    onSuccess: () => {
      toast.success("Entry removed from waitlist");
      queryClient.invalidateQueries({ queryKey: ["waitlist"] });
    },
    onError: (err: Error) => toast.error("Failed to remove entry", err.message),
  });

  usePolling([["waitlist"]], 30_000);

  if (isLoading && !data) {
    return (
      <div>
        <PageHeader title="Waitlist" subtitle="Signups awaiting onboarding" />
        <div className="flex items-center justify-center py-20">
          <Spinner size={24} />
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <PageHeader title="Waitlist" subtitle="Signups awaiting onboarding" />
        <QueryError error={error} queryKeys={[["waitlist"]]} />
      </div>
    );
  }

  const entries = data?.entries ?? [];

  return (
    <div>
      <PageHeader title="Waitlist" subtitle="Signups awaiting onboarding" />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-8">
        <StatCard title="Total Signups" value={String(data?.total ?? 0)} />
        <StatCard title="Pending Approval" value={String(data?.pending ?? 0)} />
        <StatCard title="Approved" value={String(data?.approved ?? 0)} />
        <StatCard
          title="Latest Signup"
          value={
            entries.length > 0 ? formatDateOnly(entries[entries.length - 1]?.created_at) : "--"
          }
        />
        <StatCard title="Sources" value={String(new Set(entries.map((e) => e.source)).size)} />
      </div>

      <Card padding={false}>
        <Table
          columns={[
            { key: "email", header: "Email" },
            { key: "source", header: "Source" },
            {
              key: "status",
              header: "Status",
              render: (row: WaitlistEntry) => (
                <Badge color={row.status === "approved" ? "green" : "secondary"} dot>
                  {row.status}
                </Badge>
              ),
            },
            {
              key: "created_at",
              header: "Signed Up",
              render: (row: WaitlistEntry) => (
                <span style={{ color: "var(--c-text-secondary)" }}>
                  {formatDateTime(row.created_at)}
                </span>
              ),
            },
            {
              key: "welcome_sent_at",
              header: "Login Email",
              render: (row: WaitlistEntry) => {
                if (!row.welcome_sent_at) {
                  return <span style={{ color: "var(--c-text-faint)" }}>Not sent</span>;
                }
                return (
                  <span style={{ color: "var(--c-text-secondary)" }}>
                    {formatDateTime(row.welcome_sent_at)}
                  </span>
                );
              },
            },
            {
              key: "actions",
              header: "",
              render: (row: WaitlistEntry) => (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      sendWelcome.mutate({
                        id: row.id,
                        email: row.email,
                        resend: row.status === "approved",
                      })
                    }
                    disabled={sendWelcome.isPending}
                  >
                    {row.status === "approved" ? "Resend Login Email" : "Approve & Notify"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeEntry.mutate(row.id)}
                    disabled={removeEntry.isPending}
                  >
                    Remove
                  </Button>
                </div>
              ),
            },
          ]}
          data={entries as unknown as Record<string, unknown>[]}
          keyField="id"
          emptyMessage="No waitlist signups"
        />
      </Card>
    </div>
  );
}
